
import { inject, Injectable } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { AuthService } from './auth.service';
import { UserRole } from '../models/enums';
import { Resource } from '../models/resource.model';
import { ResourceService } from '../models/resource-service.model';
import { ResourceAvailability } from '../models/resource-availability.model';
import { PaginatedResponse } from '../models/pagination.model';

@Injectable({
    providedIn: 'root'
})
export class StaffService {
    private supabase = inject(SupabaseService).client;
    private authService = inject(AuthService); // Inject AuthService

    async getResources(businessId: string | null, branchId?: string | null): Promise<Resource[]> {
        // Usamos LEFT JOIN (!) para evitar el "Join Trap" por defecto.
        // Si hay branchId, cambiamos a !inner para filtrar los resultados principales
        // basado en la existencia de la relación en branch_resources.
        const branchJoin = branchId ? 'branch_resources!inner' : 'branch_resources';
        
        const select = `
            *,
            business:businesses(name),
            profile:profiles(
                role
            ),
            ${branchJoin}(
                branch_id,
                branch:branches(name)
            )
        `;

        let query = this.supabase
            .from('resources')
            .select(select)
            .eq('type', 'staff')
            .order('name');

        // Lógica de Filtro corregida:
        // 1. Si hay branchId, filtramos por la tabla intermedia branch_resources (usando !inner).
        // 2. Si no hay branchId pero hay businessId, filtramos por empresa.
        if (branchId) {
            query = query.eq('branch_resources.branch_id', branchId);
        } else if (businessId) {
            query = query.eq('business_id', businessId);
        }

        const { data, error } = await query;

        if (error) throw error;

        // Flattening / Mapping
        return (data || []).map((item: any) => ({
            ...item,
            business_name: item.business?.name,
            role: item.profile?.role,
            branch_names: item.branch_resources?.map((br: any) => br.branch?.name).filter(Boolean) || [],
            branch_ids: item.branch_resources?.map((br: any) => br.branch_id) || []
        })) as Resource[];
    }

    /**
     * Obtiene los recursos (staff) paginados con soporte para búsqueda y filtros.
     */
    async getResourcesPaginated(params: {
        businessId: string | null,
        branchId?: string | null,
        search?: string,
        pageIndex?: number,
        pageSize?: number
    }): Promise<PaginatedResponse<Resource>> {
        const branchJoin = params.branchId ? 'branch_resources!inner' : 'branch_resources';
        
        const select = `
            *,
            business:businesses(name),
            profile:profiles(
                role
            ),
            ${branchJoin}(
                branch_id,
                branch:branches(name)
            )
        `;

        let query = this.supabase
            .from('resources')
            .select(select, { count: 'exact' })
            .eq('type', 'staff');

        // Filtros de empresa / sucursal
        if (params.branchId) {
            query = query.eq('branch_resources.branch_id', params.branchId);
        } else if (params.businessId) {
            query = query.eq('business_id', params.businessId);
        } else if (this.authService.userRole() !== UserRole.SUPERADMIN) {
            // Si no hay branch ni business y no es Superadmin, algo anda mal
            return { data: [], total: 0 };
        }

        // Búsqueda
        if (params.search) {
            const search = `%${params.search}%`;
            query = query.or(`name.ilike.${search},email.ilike.${search}`);
        }

        // Paginación
        if (params.pageIndex !== undefined && params.pageSize !== undefined) {
            const fromIdx = params.pageIndex * params.pageSize;
            const toIdx = fromIdx + params.pageSize - 1;
            query = query.range(fromIdx, toIdx);
        }

        const { data, error, count } = await query.order('name');

        if (error) {
            console.error('Error al obtener staff paginado:', error);
            return { data: [], total: 0 };
        }

        const mappedData = (data || []).map((item: any) => ({
            ...item,
            business_name: item.business?.name,
            role: item.profile?.role,
            branch_names: item.branch_resources?.map((br: any) => br.branch?.name).filter(Boolean) || [],
            branch_ids: item.branch_resources?.map((br: any) => br.branch_id) || []
        })) as Resource[];

        return {
            data: mappedData,
            total: count || 0
        };
    }

    async getResourceById(id: string): Promise<Resource> {
        const { data, error } = await this.supabase
            .from('resources')
            .select(`
                *,
                profiles (
                    id,
                    role
                ),
                branch_resources (
                    branch_id
                )
            `)
            .eq('id', id)
            .single();

        if (error) throw error;

        const resource = data as any;
        if (resource.profiles) {
            resource.role = resource.profiles.role;
        }
        resource.branch_ids = (resource.branch_resources || []).map((br: any) => br.branch_id);

        return resource as Resource;
    }

    async getResourceBranches(resourceId: string): Promise<string[]> {
        const { data, error } = await (this.supabase as any)
            .from('branch_resources')
            .select('branch_id')
            .eq('resource_id', resourceId);

        if (error) throw error;
        return (data || []).map((b: any) => b.branch_id);
    }

    async createResource(resource: Partial<Resource> & { role?: UserRole, branch_ids?: string[], password?: string, requires_login?: boolean }): Promise<Resource> {
        const { role, branch_ids, password, requires_login, ...resourceData } = resource;

        const businessId = resourceData.business_id || this.authService.userProfile()?.business_id;
        if (!businessId) throw new Error('Business ID is required');

        if (requires_login !== false && !resourceData.email) {
            throw new Error('Email is required for new staff');
        }

        // Vendedor sin acceso: solo se registra como recurso (sin usuario Auth,
        // sin profile, sin mail de invitación). RLS de 'resources' ya permite
        // este insert directo para admin/owner/superadmin de su propio negocio.
        if (requires_login === false) {
            const { data: resData, error: resError } = await this.supabase
                .from('resources')
                .insert({
                    business_id: businessId,
                    name: resourceData.name,
                    email: resourceData.email || null,
                    phone: resourceData.phone,
                    is_active: resourceData.is_active ?? true,
                    profile_id: null
                } as any)
                .select()
                .single();

            if (resError) throw resError;

            if (branch_ids?.length) {
                const resourceInserts = branch_ids.map(bid => ({ resource_id: (resData as any).id, branch_id: bid }));
                const { error: brError } = await this.supabase.from('branch_resources').insert(resourceInserts as any);
                if (brError) throw brError;
            }

            return resData as Resource;
        }

        const session = this.authService.session();
        const token = session?.access_token;

        const { data, error } = await this.supabase.functions.invoke('manage-staff-auth', {
            body: {
                action: 'createUser',
                email: resourceData.email,
                name: resourceData.name,
                business_id: businessId,
                role: role,
                branch_ids: branch_ids,
                phone: resourceData.phone,
                color: (resourceData.details as any)?.color,
                origin: window.location.origin
            },
            headers: token ? { Authorization: `Bearer ${token}` } : undefined
        });

        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        return data.resource as Resource;
    }

    /**
     * @description Reenvía el correo de invitación o recuperación de clave a un profesional.
     * @param email Correo electrónico del miembro del equipo.
     * @param name Nombre del profesional (opcional).
     * @param businessId ID del negocio (opcional).
     */
    async resendInvitation(email: string, name?: string, businessId?: string): Promise<boolean> {
        if (!email) throw new Error('El email es obligatorio para reenviar la invitación');

        const session = this.authService.session();
        const token = session?.access_token;

        const { data, error } = await this.supabase.functions.invoke('manage-staff-auth', {
            body: {
                action: 'resendInvite',
                email,
                name,
                business_id: businessId,
                origin: window.location.origin
            },
            headers: token ? { Authorization: `Bearer ${token}` } : undefined
        });

        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        return true;
    }

    async updateResource(id: string, resource: Partial<Resource> & { role?: UserRole, branch_ids?: string[], password?: string }): Promise<Resource> {
        const { role, branch_ids, password, ...resourceData } = resource;

        let profileId = resource.profile_id;
        if (!profileId) {
            const { data } = await this.supabase
                .from('resources')
                .select('profile_id')
                .eq('id', id)
                .single();

            // Cast strictly to ensure TS is happy
            const res = data as any;
            profileId = res?.profile_id;
        }

        if (!profileId) {
            // Vendedor sin acceso: no hay usuario Auth que actualizar, se
            // actualiza el recurso directo y se resincronizan sus sucursales.
            const { data: resData, error: resError } = await this.supabase
                .from('resources')
                .update({
                    name: resourceData.name,
                    email: resourceData.email || null,
                    phone: resourceData.phone,
                    is_active: resourceData.is_active
                } as any)
                .eq('id', id)
                .select()
                .single();

            if (resError) throw resError;

            if (branch_ids) {
                await this.supabase.from('branch_resources').delete().eq('resource_id', id);
                if (branch_ids.length > 0) {
                    const resourceInserts = branch_ids.map(bid => ({ resource_id: id, branch_id: bid }));
                    const { error: brError } = await this.supabase.from('branch_resources').insert(resourceInserts as any);
                    if (brError) throw brError;
                }
            }

            return resData as Resource;
        }

        const { data, error } = await this.supabase.functions.invoke('manage-staff-auth', {
            body: {
                action: 'updateStaff',
                userId: profileId,
                resourceId: id,
                ...resourceData,
                role,
                branch_ids,
                password
            }
        });

        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        return data.resource as Resource;
    }

    async toggleStatus(id: string, isActive: boolean): Promise<Resource> {
        const { data, error } = await this.supabase
            .from('resources')
            .update({ is_active: isActive } as any)
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return data as Resource;
    }

    // Services
    /**
     * @description Obtiene los servicios asignados a un profesional.
     * @param resourceId ID del profesional.
     * @param branchId ID de la sucursal (opcional).
     */
    async getResourceServices(resourceId: string, branchId?: string): Promise<any[]> {
        let query = this.supabase
            .from('resource_services')
            .select('*, services(title)')
            .eq('resource_id', resourceId);

        if (branchId) {
            query = query.eq('branch_id', branchId);
        }

        const { data, error } = await query;
        if (error) throw error;
        return data;
    }

    /**
     * @description Sincroniza los servicios asignados a un profesional en una sucursal específica (M:N:M).
     * @param resourceId ID del profesional.
     * @param businessId ID de la empresa.
     * @param branchId ID de la sucursal.
     * @param serviceIds Arreglo de IDs de servicios asignados.
     */
    async syncResourceServices(resourceId: string, businessId: string, branchId: string, serviceIds: string[]): Promise<void> {
        // 1. Eliminar vínculos existentes PARA ESTA SUCURSAL
        const { error: deleteError } = await this.supabase
            .from('resource_services')
            .delete()
            .eq('resource_id', resourceId)
            .eq('branch_id', branchId);

        if (deleteError) throw deleteError;

        // 2. Insertar nuevos vínculos
        if (serviceIds.length > 0) {
            const inserts = serviceIds.map(serviceId => ({
                resource_id: resourceId,
                service_id: serviceId,
                business_id: businessId,
                branch_id: branchId
            }));

            const { error: insertError } = await this.supabase
                .from('resource_services')
                .insert(inserts as any[]);

            if (insertError) throw insertError;
        }
    }

    // Availability
    async getAvailability(resourceId: string, branchId?: string): Promise<ResourceAvailability[]> {
        let query = this.supabase
            .from('resource_availability')
            .select('*')
            .eq('resource_id', resourceId)
            .order('day_of_week');

        if (branchId) {
            query = query.eq('branch_id', branchId);
        }

        const { data, error } = await query;
        if (error) throw error;
        return data as ResourceAvailability[];
    }

    async updateAvailability(availability: any): Promise<ResourceAvailability> {
        const { data, error } = await this.supabase
            .from('resource_availability')
            .upsert(availability)
            .select()
            .single();

        if (error) throw error;
        return data as ResourceAvailability;
    }

    async syncAvailability(resourceId: string, businessId: string, branchId: string, availabilities: any[], serviceIds: string[] | 'all'): Promise<void> {
        // Build delete query
        let deleteQuery = this.supabase
            .from('resource_availability')
            .delete()
            .eq('resource_id', resourceId)
            .eq('branch_id', branchId);

        // Limpieza quirúrgica: solo borrar lo que solapa con los servicios en edición
        if (serviceIds === 'all') {
            deleteQuery = deleteQuery.is('service_ids', null);
        } else if (Array.isArray(serviceIds) && serviceIds.length > 0) {
            // Usamos overlaps para asegurar que borramos solo lo que toca a estos servicios
            deleteQuery = deleteQuery.overlaps('service_ids', serviceIds);
        }

        const { error: delError } = await deleteQuery;
        if (delError) throw delError;

        // Insert new ones
        if (availabilities.length > 0) {
            // Aseguramos que cada registro tenga los IDs base requeridos (NOT NULL)
            const toInsert = availabilities.map(a => ({
                ...a,
                resource_id: resourceId,
                business_id: businessId,
                branch_id: branchId
            }));

            const { error: insError } = await this.supabase
                .from('resource_availability')
                .insert(toInsert);
                
            if (insError) throw insError;
        }
    }

    async deleteResource(id: string, profileId?: string): Promise<void> {
        // If linked to an Auth User, delete via Edge Function (cascading)
        if (profileId) {
            const session = this.authService.session();
            const token = session?.access_token;

            const { data, error } = await this.supabase.functions.invoke('manage-staff-auth', {
                body: { action: 'deleteStaff', userId: profileId },
                headers: token ? { Authorization: `Bearer ${token}` } : undefined
            });

            if (error) throw error;
            if (data?.error) throw new Error(data.error);
        } else {
            // If just a resource (no auth), delete directly
            const { error } = await this.supabase
                .from('resources')
                .delete()
                .eq('id', id);

            if (error) throw error;
        }
    }
}
